import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { errorText } from '../config';
import { useAuth } from '../context/AuthContext';
import {
  Activity, AlertTriangle, AlertOctagon, BellRing, CheckCircle2, Loader2,
  RefreshCw, Mail, ShieldCheck, TrendingUp, TrendingDown, Minus, Search,
} from 'lucide-react';

const STATUS_STYLES = {
  healthy: { label: 'Healthy', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  warning: { label: 'Warning', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  critical: { label: 'Critical', badge: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  no_data: { label: 'No data', badge: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-300' },
};

const SEVERITY_STYLES = {
  critical: 'bg-rose-50 text-rose-700 border-rose-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-slate-100 text-slate-600 border-slate-200',
};

function TrendIcon({ trend }) {
  if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-rose-600" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
}

export default function Monitoring() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Caply_Admin';
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [filter, setFilter] = useState('');
  const [alertFilter, setAlertFilter] = useState('all');
  const [resolveBusy, setResolveBusy] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.get('/api/monitoring/summary'),
      api.get('/api/monitoring/portfolio'),
      api.get('/api/monitoring/alerts'),
    ])
      .then(([s, p, a]) => {
        setSummary(s.data);
        setStatuses(p.data);
        setAlerts(a.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    setCheckBusy(true); setCheckError(null); setCheckResult(null);
    try {
      const res = await api.post('/api/monitoring/evaluate');
      setCheckResult(res.data);
      load();
    } catch (err) {
      setCheckError(errorText(err, 'Check failed — API unreachable.'));
    } finally { setCheckBusy(false); }
  };

  const resolveAlert = async (alertId) => {
    setResolveBusy(alertId);
    try {
      await api.post(`/api/monitoring/alerts/${alertId}/resolve`);
      setAlerts(prev => prev.filter(a => (a.alert_id || a.id) !== alertId));
      load();
    } finally { setResolveBusy(null); }
  };

  const visibleAlerts = alerts.filter(a => {
    if (alertFilter !== 'all' && a.severity !== alertFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (a.company_name || a.portco_id || '').toLowerCase().includes(q) || (a.message || '').toLowerCase().includes(q);
  });

  const visibleStatuses = statuses.filter(s =>
    !filter || (s.company_name || '').toLowerCase().includes(filter.toLowerCase()));

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center gap-3 text-slate-400 py-24 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading monitoring…
        </div>
      </div>
    );
  }

  const sev = summary?.alerts_by_severity || {};

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="page-title">Alerts</h2>
          <p className="page-subtitle">
            Caply watches each company’s reported numbers and flags anything heading the wrong way — no invented metrics, ever.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-primary flex items-center gap-2" onClick={runCheck} disabled={checkBusy}>
            {checkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {checkBusy ? 'Checking…' : 'Check now'}
          </button>
          <button className="btn-secondary flex items-center gap-2" onClick={load}>
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Check result banner */}
      {checkResult && (
        <div className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50/60">
          <div className="flex items-center gap-2 text-sm font-medium text-navy-900">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Checked {checkResult.evaluated} company{checkResult.evaluated === 1 ? '' : 's'} ·
            {' '}{checkResult.alerts_created.length} new alert{checkResult.alerts_created.length === 1 ? '' : 's'}
            {checkResult.existing_active > 0 && ` · ${checkResult.existing_active} already active (not duplicated)`}
          </div>
          {checkResult.digest?.hint && (
            <p className="text-xs text-amber-700 mt-1.5">{checkResult.digest.hint}</p>
          )}
        </div>
      )}
      {checkError && (
        <div className="mt-4 p-4 rounded-lg border border-rose-200 bg-rose-50 text-sm text-rose-700">{checkError}</div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        <div className="kpi-card relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-rose-500/80" />
          <p className="kpi-label">Critical alerts</p>
          <p className="kpi-value">{sev.critical || 0}</p>
        </div>
        <div className="kpi-card relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-amber-500/80" />
          <p className="kpi-label">Warnings</p>
          <p className="kpi-value">{sev.warning || 0}</p>
        </div>
        <div className="kpi-card relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500/80" />
          <p className="kpi-label">Companies healthy</p>
          <p className="kpi-value">{summary?.healthy || 0}<span className="text-slate-400 text-lg">/{summary?.total_companies || 0}</span></p>
        </div>
        <div className="kpi-card relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-rose-500/80" />
          <p className="kpi-label">Companies critical</p>
          <p className="kpi-value">{summary?.critical || 0}</p>
        </div>
        <div className="kpi-card relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-slate-400/80" />
          <p className="kpi-label">No data reported</p>
          <p className="kpi-value">{summary?.no_data || 0}</p>
        </div>
      </div>

      {/* Search + severity filter */}
      <div className="flex flex-wrap items-center gap-3 mt-8">
        <div className="relative flex-1 min-w-56">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="form-input pl-9"
            placeholder="Filter by company or message…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1.5">
          {['all', 'critical', 'warning'].map(f => (
            <button
              key={f}
              onClick={() => setAlertFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm capitalize transition border ${
                alertFilter === f
                  ? 'bg-navy-900 text-white border-navy-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {f === 'all' ? 'All alerts' : f}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          Email digests configured in Settings → Integrations
        </span>
      </div>

      {/* Alerts */}
      <div className="mt-4 space-y-3">
        {visibleAlerts.length === 0 && (
          <div className="card p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
            <p className="text-sm text-slate-600 mt-2">
              No active alerts {alerts.length === 0 ? '— run “Check now” to evaluate your latest reported figures against stage-aware thresholds.' : 'matching this filter.'}
            </p>
          </div>
        )}
        {visibleAlerts.map(a => {
          const id = a.alert_id || a.id;
          return (
            <div key={id} className={`card p-4 border ${a.severity === 'critical' ? 'border-rose-200' : a.severity === 'warning' ? 'border-amber-200' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase border ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info}`}>
                      {a.severity}
                    </span>
                    <span className="text-sm font-semibold text-navy-900">{a.company_name || a.portco_id}</span>
                    {a.period && <span className="text-xs text-slate-400">· {a.period}</span>}
                    <span className="text-xs text-slate-400">· {a.category}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{a.message}</p>
                  <p className="text-xs text-slate-400 mt-1">Triggered {new Date(a.triggered_at).toLocaleString()}</p>
                </div>
                <button
                  className="btn-secondary text-xs flex items-center gap-1.5 shrink-0"
                  onClick={() => resolveAlert(id)}
                  disabled={resolveBusy === id}
                >
                  {resolveBusy === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Resolve
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Portfolio health */}
      <h3 className="text-base font-semibold text-navy-900 mt-10 mb-4 flex items-center gap-2">
        <BellRing className="w-4 h-4 text-brand-600" /> Portfolio health
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleStatuses.map(s => {
          const st = STATUS_STYLES[s.status] || STATUS_STYLES.no_data;
          return (
            <div key={s.portco_id} className="card p-4 cursor-pointer hover:shadow-md transition"
              onClick={() => navigate(`/portfolio/${s.portco_id}`)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                  <p className="text-sm font-semibold text-navy-900">{s.company_name}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${st.badge}`}>{st.label}</span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><TrendIcon trend={s.trend} /> {s.trend}</span>
                <span>Health {s.health_score}</span>
                <span>{s.alerts_count} alert{s.alerts_count === 1 ? '' : 's'}</span>
                <span>{s.quarters_reported} qtr{s.quarters_reported === 1 ? '' : 's'} reported</span>
              </div>
              {(s.risk_factors || []).length > 0 && (
                <p className="text-xs text-slate-500 mt-2 line-clamp-2">{s.risk_factors[0]}</p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 mt-8 max-w-3xl leading-relaxed">
        Alerts are computed only from quarterly figures your firm reported (revenue, EBITDA, margin) and stage-aware
        thresholds. Metrics Caply doesn't ingest yet (cash runway, DSO, retention, leverage) are never guessed.
        Caply automatically re-evaluates daily and can email a digest — configure recipients in Settings → Integrations.
        {' '}{!isAdmin && '(Email settings are firm-admin only.)'}
      </p>
    </div>
  );
}
